/**
 * Detail panel for the selected location.
 *
 * Three stacked sections — assignment (who works/manages/runs here), patients
 * (the unit roster), and labels — over a breadcrumb that shows where the unit
 * sits. Member principals are resolved to human names (people via the admin user
 * list, roles via the role catalogue) so an admin reads "ICU Nurses", not a UUID.
 *
 * This panel owns its own location-scoped dialogs (edit / move / assign / admit /
 * delete). The tree-scoped create dialogs live in LocationsView.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@/components/ui/button';
import { useAdminUsers } from '@/hooks/useAdminAnalytics';
import { formatPatientLabel } from '@/lib/patientLabel';
import { usePatientsConfig } from '@/hooks/usePatients';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { StaleDataNotice } from '@/components/clinical';
import {
  CountBadge,
  EmptyState,
  ErrorState,
  KindChip,
  LoadingRows,
  Panel,
  RemovableRow,
  RoleBadge,
  SectionHeader,
  errMessage,
  isForbidden,
} from './OrgUi';
import { AccessRequestsSection, RequestAccessInline } from './AccessRequests';
import { LocationSchemaSection } from './LocationSchemaSection';
import {
  useLocationAncestors,
  useLocationMembers,
  useLocationMembersBatch,
  useLocationPatients,
  useRoles,
  useRoleMembersBatch,
  useRemoveLocationMember,
  useDischargePatient,
  useDeleteLocation,
  indexRolesById,
  indexUsersById,
  type RoleMembersState,
} from './useOrg';
import {
  LocationFormDialog,
  MoveLocationDialog,
  AddLocationMemberDialog,
  AdmitPatientDialog,
} from './OrgDialogs';
import { collectDescendantIds } from './tree';
import type { Location, LocationMember, OrgRole } from '@/api/endpoints/org';
import type { PatientResponse } from '@/api/endpoints/patients';

export function LocationDetail({
  location,
  allLocations,
  patientCounts,
  onDeleted,
}: {
  location: Location;
  allLocations: Location[];
  patientCounts: Map<string, number>;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [admitting, setAdmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const ancestors = useLocationAncestors(location.id);
  const breadcrumb = ancestors.data?.ancestors ?? [];

  // Direct child units, from the already-loaded full set. A unit WITH children is
  // a parent (building/hospital) → its detail shows a per-child census breakdown;
  // a unit with none is a leaf (ward/bed) → it shows the flat patient roster.
  const childUnits = useMemo(
    () => allLocations.filter((l) => l.parent_id === location.id),
    [allLocations, location.id],
  );
  // Every unit beneath this one (visible subtree) — for aggregating who staffs
  // the whole subtree on a parent's access panel.
  const descendantIds = useMemo(
    () => collectDescendantIds(allLocations, location.id),
    [allLocations, location.id],
  );

  return (
    <div className="space-y-6">
      {/* Breadcrumb + header */}
      <div className="space-y-2">
        {breadcrumb.length > 0 && (
          <nav className="flex flex-wrap items-center gap-1 text-xs text-neutral-400">
            {breadcrumb.map((a) => (
              <span key={a.id} className="flex items-center gap-1">
                {a.name}
                <span className="text-neutral-300">/</span>
              </span>
            ))}
            <span className="text-neutral-500">{location.name}</span>
          </nav>
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-neutral-900">{location.name}</h2>
            <KindChip kind={location.kind} />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              {t('common.edit', 'Edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMoving(true)}>
              {t('org.move.button', 'Move')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setConfirmDelete(true)}
            >
              {t('common.delete', 'Delete')}
            </Button>
          </div>
        </div>
      </div>

      <MembersSection location={location} descendantIds={descendantIds} onAssign={() => setAssigning(true)} />
      <AccessRequestsSection location={location} />
      <PatientsSection
        location={location}
        childUnits={childUnits}
        patientCounts={patientCounts}
        onAdmit={() => setAdmitting(true)}
      />
      <LocationSchemaSection location={location} />
      <LabelsSection location={location} />

      {/* Dialogs */}
      <LocationFormDialog open={editing} onOpenChange={setEditing} location={location} />
      <MoveLocationDialog open={moving} onOpenChange={setMoving} location={location} allLocations={allLocations} />
      <AddLocationMemberDialog open={assigning} onOpenChange={setAssigning} locationId={location.id} locationName={location.name} />
      <AdmitPatientDialog open={admitting} onOpenChange={setAdmitting} locationId={location.id} locationName={location.name} />
      <DeleteLocationDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        location={location}
        onDeleted={onDeleted}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Members
// -----------------------------------------------------------------------------

// ACL tiers, strongest first (owner ⊇ editor ⊇ viewer). Explicit order so members
// always render strongest-first; the per-tier heading is resolved at render via
// tierLabel (i18n).
const ACL_TIER_ORDER: OrgRole[] = ['owner', 'editor', 'viewer'];

// Plain-language group heading for an ACL tier. The switch is EXHAUSTIVE: adding a
// tier to the `OrgRole` wire union without a branch here is a compile error (the
// `never` assignment), so a new tier can never silently render its members under
// no header.
function tierLabel(tier: OrgRole, t: TFunction): string {
  switch (tier) {
    case 'owner':
      return t('org.locationDetail.tier.owner', 'Owners — run this unit');
    case 'editor':
      return t('org.locationDetail.tier.editor', 'Editors — manage this unit');
    case 'viewer':
      return t('org.locationDetail.tier.viewer', 'Viewers — work in this unit');
    default: {
      const _exhaustive: never = tier;
      return _exhaustive;
    }
  }
}

// Strength order (owner ⊇ editor ⊇ viewer) for collapsing a principal's several
// grants across a subtree to the single highest role they hold anywhere in it.
const ROLE_RANK: Record<OrgRole, number> = { owner: 3, editor: 2, viewer: 1 };
function roleRank(role: OrgRole): number {
  return ROLE_RANK[role];
}

export function MembersSection({
  location,
  descendantIds,
  onAssign,
}: {
  location: Location;
  descendantIds: string[];
  onAssign: () => void;
}) {
  const { t } = useTranslation();
  const members = useLocationMembers(location.id);
  const { data: users } = useAdminUsers();
  const { data: rolesData } = useRoles();
  const remove = useRemoveLocationMember();
  const [pending, setPending] = useState<string | null>(null);

  const userById = useMemo(() => indexUsersById(users), [users]);
  const rolesById = useMemo(() => indexRolesById(rolesData), [rolesData]);

  // Keyed by PRINCIPAL, not principal-and-role: removing access removes the
  // principal's grant on this unit, so the row and the button address the same
  // thing. The list is already one row per principal (see listLocationMembers).
  const memberKey = (m: LocationMember) => `${m.principal_type}:${m.principal_id}`;
  const roleLabel = (id: string) => rolesById[id]?.name ?? id;
  const doRemove = (m: LocationMember) => {
    setPending(memberKey(m));
    remove.mutate({ id: location.id, member: m }, { onSettled: () => setPending(null) });
  };

  // Partition the node's grants: CUJ-84 clinical roles are OpenFGA groups
  // (principal_type 'group'); everyone else is a person granted directly. We
  // group rather than ship a flat list — a role staffing the unit reads as the
  // role, named people read under their ACL tier. The switch is EXHAUSTIVE: a new
  // principal_type added to the wire union without a branch here is a compile
  // error (the `never` assignment), so a grant can never silently vanish from the
  // panel while still counting toward the header total.
  const { roleGrants, userGrants } = useMemo(() => {
    const roles: LocationMember[] = [];
    const usersList: LocationMember[] = [];
    for (const m of members.data?.items ?? []) {
      switch (m.principal_type) {
        case 'group':
          roles.push(m);
          break;
        case 'user':
          usersList.push(m);
          break;
        default: {
          const _exhaustive: never = m.principal_type;
          void _exhaustive;
        }
      }
    }
    return { roleGrants: roles, userGrants: usersList };
  }, [members.data]);
  const directCount = roleGrants.length + userGrants.length;

  // INHERITED access. Access cascades DOWN the tree — the model grants
  // owner/editor/viewer `from parent`, so anyone granted on an ancestor unit also
  // has that access here (and, via patient.viewer `from location`, sees this
  // unit's patients). A panel that listed only the node's OWN grants would tell an
  // admin a ward has "no one" while a physio granted at the hospital quietly sees
  // every patient. We surface those: the ancestor chain (already loaded for the
  // breadcrumb, so no extra fetch) plus each ancestor's grants, tagged with the
  // unit they flow from. Each ancestor query is ACL-gated server-side, so an
  // ancestor the admin cannot view contributes nothing (never a leak).
  // Both the chain and the flattened rows are memoised: `accessCount` and the
  // role-member batch below take them as inputs, and a fresh array on every
  // render re-ran that dedup (and re-keyed the batch) for no change in data.
  const ancestors = useLocationAncestors(location.id).data?.ancestors;
  const ancestorChain = useMemo(() => ancestors ?? [], [ancestors]);
  const ancestorMembers = useLocationMembersBatch(ancestorChain.map((a) => a.id));
  const inherited = useMemo(() => {
    const rows: { member: LocationMember; sourceName: string }[] = [];
    for (const a of ancestorChain) {
      for (const m of ancestorMembers[a.id]?.members ?? []) rows.push({ member: m, sourceName: a.name });
    }
    return rows;
  }, [ancestorChain, ancestorMembers]);
  const inheritedLoading = ancestorChain.some((a) => ancestorMembers[a.id]?.isLoading);

  // SUBTREE staffing — the up-tree counterpart for a parent unit: who works in any
  // sub-unit beneath this one. Grants live on individual leaf units; we aggregate
  // every descendant's grants (each fetch ACL-gated, so an unviewable sub-unit
  // contributes nothing) into the distinct principals staffing the subtree, each
  // shown at its STRONGEST role across the subtree. Empty for a leaf (no
  // descendants → no queries fire).
  const subtreeMembers = useLocationMembersBatch(descendantIds);
  const subtreeStaff = useMemo(() => {
    const strongest = new Map<string, LocationMember>();
    for (const id of descendantIds) {
      for (const m of subtreeMembers[id]?.members ?? []) {
        const key = `${m.principal_type}:${m.principal_id}`;
        const cur = strongest.get(key);
        if (!cur || roleRank(m.role) > roleRank(cur.role)) strongest.set(key, m);
      }
    }
    return [...strongest.values()];
  }, [descendantIds, subtreeMembers]);
  const subtreeLoading = descendantIds.some((id) => subtreeMembers[id]?.isLoading);

  // Header count = distinct principals with access shown in this panel (granted
  // here, inherited from an ancestor, or staffing a sub-unit), deduped by
  // principal so the same person/role counted once. `directCount` alone would
  // render "Who has access (0)" beneath a list of inherited grants — the header
  // must agree with the body. Inherited carries one entry per source unit, so
  // dedup is required, not cosmetic.
  const accessCount = useMemo(() => {
    const seen = new Set<string>();
    for (const m of roleGrants) seen.add(`${m.principal_type}:${m.principal_id}`);
    for (const m of userGrants) seen.add(`${m.principal_type}:${m.principal_id}`);
    for (const { member: m } of inherited) seen.add(`${m.principal_type}:${m.principal_id}`);
    for (const m of subtreeStaff) seen.add(`${m.principal_type}:${m.principal_id}`);
    return seen.size;
  }, [roleGrants, userGrants, inherited, subtreeStaff]);

  // Resolve every role grant's members from ONE place (one query per distinct
  // role, deduped/cached) so the rows below stay pure — no per-row fetch hook.
  // Covers the node's own role grants AND inherited AND subtree ones, so any role
  // (e.g. "Physiotherapy" granted at the hospital, or "ICU Nurses" staffing a ward
  // below) names its people too.
  const roleMembersById = useRoleMembersBatch([
    ...roleGrants.map((m) => m.principal_id),
    ...inherited.filter((g) => g.member.principal_type === 'group').map((g) => g.member.principal_id),
    ...subtreeStaff.filter((m) => m.principal_type === 'group').map((m) => m.principal_id),
  ]);

  return (
    <Panel className="p-4">
      <SectionHeader
        title={t('org.locationDetail.whoHasAccess', 'Who has access')}
        count={accessCount}
        action={
          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={onAssign}>
            {t('org.locationDetail.assign', '+ Assign')}
          </Button>
        }
      />
      <div className="mt-3">
        {/* A background refetch failing (or in flight) with a cached access
            list still showing must stay visible — ACL membership is exactly
            the kind of fact that must never look more current than it is. */}
        {members.data && <StaleDataNotice isFetching={members.isFetching} isError={members.isError} className="mb-2" />}
        {members.isError && !members.data ? (
          <div className="space-y-3">
            <ErrorState error={members.error} onRetry={() => void members.refetch()} />
            {isForbidden(members.error) && <RequestAccessInline locationId={location.id} />}
          </div>
        ) : members.isLoading ? (
          <LoadingRows />
        ) : directCount === 0 &&
          inherited.length === 0 &&
          subtreeStaff.length === 0 &&
          !inheritedLoading &&
          !subtreeLoading ? (
          <EmptyState
            title={t('org.locationDetail.noAccess', 'No one assigned yet')}
            hint={t(
              'org.locationDetail.noAccessHint',
              'Assign a person or a role. Viewer works here, editor manages it, owner runs it — and access cascades to every sub-unit.',
            )}
            action={
              <Button size="sm" variant="outline" onClick={onAssign}>
                {t('org.locationDetail.assignSomeone', 'Assign someone')}
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            {/* Roles staffing this unit — each role plus the people in it. */}
            {roleGrants.length > 0 && (
              <div className="space-y-1">
                <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                  {t('org.locationDetail.byRole', 'By role')}
                </p>
                {roleGrants.map((m) => (
                  <RoleGrantRow
                    key={memberKey(m)}
                    member={m}
                    roleName={roleLabel(m.principal_id)}
                    userById={userById}
                    memberState={roleMembersById[m.principal_id] ?? EMPTY_ROLE_MEMBERS}
                    removing={pending === memberKey(m)}
                    onRemove={() => doRemove(m)}
                  />
                ))}
              </div>
            )}

            {/* People granted directly, grouped by their ACL tier. */}
            {userGrants.length > 0 &&
              ACL_TIER_ORDER.filter((tier) => userGrants.some((m) => m.role === tier)).map((tier) => (
                <div key={tier} className="space-y-1">
                  {/* The heading names the tier a principal HOLDS here, not a
                      membership that can be removed on its own: one row per
                      principal, at their strongest tier, and removing it
                      removes their access to the unit. */}
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                    {tierLabel(tier, t)}
                  </p>
                  {userGrants
                    .filter((m) => m.role === tier)
                    .map((m) => (
                      <RemovableRow
                        key={memberKey(m)}
                        removing={pending === memberKey(m)}
                        onRemove={() => doRemove(m)}
                        // The button removes the PRINCIPAL'S ACCESS to this
                        // unit, not the tier heading it sits under. The share
                        // contract revokes every grant a principal holds here,
                        // so an operator stepping a charge nurse down from
                        // owner would otherwise read this as "remove
                        // ownership" and take away their ward access entirely.
                        removeLabel={t('org.locationDetail.removeAccess', {
                          defaultValue: "Remove {{name}}'s access to this unit",
                          name: userById[m.principal_id] ?? m.principal_id,
                        })}
                      >
                        <span className="truncate text-sm text-neutral-800">
                          {userById[m.principal_id] ?? m.principal_id}
                        </span>
                      </RemovableRow>
                    ))}
                </div>
              ))}

            {/* Inherited from ancestor units — cascades down via `from parent`.
                Read-only here: an inherited grant is removed at the unit it lives
                on, not this one. */}
            {inherited.length > 0 && (
              <div className="space-y-1">
                <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                  {t('org.locationDetail.inherited', 'Inherited from parent units')}
                </p>
                {inherited.map(({ member: m, sourceName }) => (
                  <ReadOnlyGrantRow
                    key={`${sourceName}:${memberKey(m)}`}
                    member={m}
                    context={t('org.locationDetail.fromSource', 'from {{source}}', { source: sourceName })}
                    label={m.principal_type === 'group' ? roleLabel(m.principal_id) : userById[m.principal_id] ?? m.principal_id}
                    userById={userById}
                    memberState={m.principal_type === 'group' ? roleMembersById[m.principal_id] ?? EMPTY_ROLE_MEMBERS : undefined}
                  />
                ))}
              </div>
            )}

            {/* Staffing aggregated across every sub-unit (parent units only).
                Read-only: each grant is managed on the sub-unit it lives on. */}
            {subtreeStaff.length > 0 && (
              <div className="space-y-1">
                <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                  {t('org.locationDetail.subtreeStaffing', 'Staffing across sub-units')}
                </p>
                {subtreeStaff.map((m) => (
                  <ReadOnlyGrantRow
                    key={memberKey(m)}
                    member={m}
                    label={m.principal_type === 'group' ? roleLabel(m.principal_id) : userById[m.principal_id] ?? m.principal_id}
                    userById={userById}
                    memberState={m.principal_type === 'group' ? roleMembersById[m.principal_id] ?? EMPTY_ROLE_MEMBERS : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {remove.isError && <p className="mt-2 text-xs text-red-600">{errMessage(remove.error)}</p>}
        {remove.isError && isForbidden(remove.error) && (
          <div className="mt-2">
            <RequestAccessInline locationId={location.id} />
          </div>
        )}
      </div>
    </Panel>
  );
}

// Stable empty fallback for a role whose member query hasn't landed in the batch
// map yet — a fresh object literal per render would defeat memoisation downstream.
const EMPTY_ROLE_MEMBERS: RoleMembersState = { members: [], isLoading: false, isError: false };

// One-line description of who is in a role: the headcount plus a capped inline
// name preview. Caps the spelled-out list so a large role ("All Nurses", hundreds
// of members) does not assemble a multi-kilobyte string into the DOM (`truncate`
// only clips what is painted, not what is built); the headcount stays exact, the
// overflow collapses to "+K more". Shared by the direct and inherited role rows.
function roleMemberSummary(state: RoleMembersState, userById: Record<string, string>, t: TFunction): string {
  if (state.isError) return t('org.locationDetail.roleMembersError', 'Could not load who is in this role');
  if (state.isLoading) return t('org.locationDetail.roleMembersLoading', 'Loading members…');
  const names = state.members.map((rm) => userById[rm.user_id] ?? rm.user_id);
  if (names.length === 0) return t('org.locationDetail.roleMembersEmpty', 'No one in this role yet');
  const shown = names.slice(0, ROLE_NAME_PREVIEW).join(', ');
  const overflow = names.length - ROLE_NAME_PREVIEW;
  const list =
    overflow > 0
      ? `${shown} ${t('org.locationDetail.morePreview', '+{{count}} more', { count: overflow })}`
      : shown;
  return t('org.locationDetail.roleMemberCount', '{{count}} people: {{list}}', { count: names.length, list });
}

// One role grant: the role staffing this unit at an ACL tier, plus the named
// people who are in that role. Pure presentational row — its members come from
// the section's batched `useRoleMembersBatch` (one query per distinct role,
// deduped/cached) rather than a per-row fetch hook, so K role grants no longer
// scatter K hooks across the list.
function RoleGrantRow({
  member,
  roleName,
  userById,
  memberState,
  removing,
  onRemove,
}: {
  member: LocationMember;
  roleName: string;
  userById: Record<string, string>;
  memberState: RoleMembersState;
  removing: boolean;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <RemovableRow
      removing={removing}
      onRemove={onRemove}
      removeLabel={t('org.locationDetail.removeRoleAccess', {
        defaultValue: "Remove the {{role}} role's access to this unit",
        role: roleName,
      })}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-neutral-800">{roleName}</span>
          <span className="text-[11px] uppercase tracking-wide text-neutral-400">
            {t('org.locationDetail.roleTag', 'role')}
          </span>
          <RoleBadge role={member.role} />
        </div>
        <p className="mt-0.5 truncate text-xs text-neutral-400">{roleMemberSummary(memberState, userById, t)}</p>
      </div>
    </RemovableRow>
  );
}

// A read-only access row: a person or role that has access here NOT by a grant on
// this unit — inherited from an ancestor (`context="from <unit>"`) or staffing a
// sub-unit (no context, the section header says so). No remove control: the grant
// is managed on the unit it actually lives on. For a role, the same member preview
// as the direct rows answers "who, exactly". The secondary line is shown only when
// it carries something (a source and/or a role preview).
function ReadOnlyGrantRow({
  member,
  label,
  context,
  userById,
  memberState,
}: {
  member: LocationMember;
  label: string;
  context?: string;
  userById: Record<string, string>;
  memberState?: RoleMembersState;
}) {
  const { t } = useTranslation();
  const isRole = member.principal_type === 'group';
  const preview = isRole && memberState ? roleMemberSummary(memberState, userById, t) : '';
  const secondary = [context, preview].filter(Boolean).join(' · ');
  return (
    <div className="rounded-lg px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm text-neutral-800">{label}</span>
        {isRole && (
          <span className="text-[11px] uppercase tracking-wide text-neutral-400">
            {t('org.locationDetail.roleTag', 'role')}
          </span>
        )}
        <RoleBadge role={member.role} />
      </div>
      {secondary && <p className="mt-0.5 truncate text-xs text-neutral-400">{secondary}</p>}
    </div>
  );
}

// How many role members to name inline before collapsing to "+K more". A small
// presentational cap (mirrors the named-const style of org.ts page sizes) — the
// exact headcount is always shown, only the spelled-out list is bounded.
const ROLE_NAME_PREVIEW = 5;

// -----------------------------------------------------------------------------
// Patients
// -----------------------------------------------------------------------------

export function PatientsSection({
  location,
  childUnits,
  patientCounts,
  onAdmit,
}: {
  location: Location;
  childUnits: Location[];
  patientCounts: Map<string, number>;
  onAdmit: () => void;
}) {
  // A unit WITH sub-units is a container (building/hospital): show the per-child
  // census breakdown (plus any patients sitting directly on it). A leaf (ward/
  // bed) shows the flat admit/discharge roster.
  if (childUnits.length > 0) {
    return <SubtreeBreakdown location={location} childUnits={childUnits} patientCounts={patientCounts} />;
  }
  return <PatientRoster location={location} onAdmit={onAdmit} />;
}

// Parent (non-leaf) view: the unit's rolled-up total, a subtotal per direct child
// (each rolled up across its own subtree), and — should any patient sit DIRECTLY
// on this container — a discharge-able roster for them.
//
// Deliberately NO "+ Admit" affordance: admitting against a non-leaf id places
// the patient on the container, outside every ward roster. The backend enforces
// leaf-only admit; the direct roster below exists only to remediate a patient who
// became "direct" because a child unit was added under their ward after admission
// — without it they would be stranded (no roster, no discharge).
function SubtreeBreakdown({
  location,
  childUnits,
  patientCounts,
}: {
  location: Location;
  childUnits: Location[];
  patientCounts: Map<string, number>;
}) {
  const { t } = useTranslation();
  const total = patientCounts.get(location.id) ?? 0;
  const directPatients = useLocationPatients(location.id);
  const direct = directPatients.data?.items ?? [];
  const sorted = useMemo(
    () => [...childUnits].sort((a, b) => a.name.localeCompare(b.name)),
    [childUnits],
  );

  return (
    <Panel className="p-4">
      <SectionHeader title={t('org.locationDetail.patientsInUnit', 'Patients in this unit')} count={total} />
      <div className="mt-3 space-y-3">
        {/* Patients sitting directly on this container — normally none, surfaced
            WITH discharge so they are never stranded. The fetch's error/loading
            states are NOT swallowed: a stranded patient hidden behind a failed or
            in-flight query is the exact clinical hazard this roster exists to
            prevent, so an error surfaces (with retry) and an in-flight fetch shows
            a skeleton rather than silently claiming "all in sub-units". */}
        {directPatients.data && (
          <StaleDataNotice isFetching={directPatients.isFetching} isError={directPatients.isError} className="mb-2" />
        )}
        {directPatients.isError && !directPatients.data ? (
          <div className="space-y-3">
            <ErrorState error={directPatients.error} onRetry={() => void directPatients.refetch()} />
            {isForbidden(directPatients.error) && <RequestAccessInline locationId={location.id} />}
          </div>
        ) : directPatients.isLoading ? (
          <LoadingRows rows={1} />
        ) : direct.length > 0 ? (
          <div className="space-y-1">
            <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
              {t('org.locationDetail.directlyInUnit', 'Directly in this unit')}
            </p>
            <PatientList location={location} patients={direct} />
          </div>
        ) : null}
        {/* Per-child census, each rolled up across that child's whole subtree. */}
        <div className="space-y-1">
          {sorted.map((c) => (
            <BreakdownRow key={c.id} name={c.name} kind={c.kind} count={patientCounts.get(c.id) ?? 0} />
          ))}
        </div>
      </div>
      <p className="mt-3 px-1 text-xs text-neutral-400">
        {t(
          'org.locationDetail.rolledUpHint',
          'Rolled up across every sub-unit. Select a sub-unit on the left to admit, discharge, or move its patients.',
        )}
      </p>
    </Panel>
  );
}

// One row of the parent breakdown: a child unit and its rolled-up subtree count.
// Display-only — navigation happens via the tree. An explicit 0 is shown on
// purpose: this is a complete census of THIS unit's children, so "Onkologie 0"
// is information (that ward is empty), not noise.
function BreakdownRow({ name, kind, count }: { name: string; kind?: string; count: number }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-neutral-50">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm text-neutral-800">{name}</span>
        {kind && <KindChip kind={kind} />}
      </div>
      <CountBadge count={count} />
    </div>
  );
}

// A discharge-able list of patients in a unit. Shared by the leaf roster and the
// container's "directly in this unit" remediation list so the row rendering and
// discharge wiring live in one place.
function PatientList({ location, patients }: { location: Location; patients: PatientResponse[] }) {
  const { t } = useTranslation();
  const patientsConfig = usePatientsConfig();
  const discharge = useDischargePatient();
  const [pending, setPending] = useState<string | null>(null);

  return (
    <div className="space-y-1">
      {patients.map((p) => (
        <RemovableRow
          key={p.token}
          removeLabel={t('org.locationDetail.discharge', 'Discharge')}
          removing={pending === p.token}
          onRemove={() => {
            setPending(p.token);
            discharge.mutate(
              { locationId: location.id, patientId: p.token },
              { onSettled: () => setPending(null) },
            );
          }}
        >
          <span className="truncate text-sm text-neutral-800">
            {formatPatientLabel(p.attributes, patientsConfig?.label_format, p.token)}
          </span>
        </RemovableRow>
      ))}
      {discharge.isError && <p className="mt-2 text-xs text-red-600">{errMessage(discharge.error)}</p>}
      {discharge.isError && isForbidden(discharge.error) && (
        <div className="mt-2">
          <RequestAccessInline locationId={location.id} />
        </div>
      )}
    </div>
  );
}

// Leaf view: the flat patient roster, with admit + per-patient discharge.
function PatientRoster({ location, onAdmit }: { location: Location; onAdmit: () => void }) {
  const { t } = useTranslation();
  const patients = useLocationPatients(location.id);
  const roster = patients.data?.items ?? [];

  return (
    <Panel className="p-4">
      <SectionHeader
        title={t('org.locationDetail.patientsInUnit', 'Patients in this unit')}
        count={patients.data?.items.length}
        action={
          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={onAdmit}>
            {t('org.locationDetail.admit', '+ Admit')}
          </Button>
        }
      />
      <div className="mt-3">
        {/* Same visibility rule as the assignment/patients sections above — a
            failed or in-flight background refresh must not look identical
            to a confirmed-current roster. */}
        {patients.data && (
          <StaleDataNotice isFetching={patients.isFetching} isError={patients.isError} className="mb-2" />
        )}
        {patients.isError && !patients.data ? (
          <div className="space-y-3">
            <ErrorState error={patients.error} onRetry={() => void patients.refetch()} />
            {isForbidden(patients.error) && <RequestAccessInline locationId={location.id} />}
          </div>
        ) : patients.isLoading ? (
          <LoadingRows rows={2} />
        ) : roster.length === 0 ? (
          <EmptyState
            title={t('org.locationDetail.noPatients', 'No patients here')}
            hint={t(
              'org.locationDetail.noPatientsHint',
              'Admit a patient to place them in this unit; staff assigned here will see them automatically.',
            )}
            action={
              <Button size="sm" variant="outline" onClick={onAdmit}>
                {t('org.locationDetail.admitPatient', 'Admit a patient')}
              </Button>
            }
          />
        ) : (
          <PatientList location={location} patients={roster} />
        )}
      </div>
    </Panel>
  );
}

// -----------------------------------------------------------------------------
// Labels (read-only display; editing is in the Edit dialog)
// -----------------------------------------------------------------------------

function LabelsSection({ location }: { location: Location }) {
  const { t } = useTranslation();
  const entries = Object.entries(location.labels ?? {});
  if (entries.length === 0) return null;
  return (
    <Panel className="p-4">
      <SectionHeader title={t('org.locationDetail.labels', 'Labels')} />
      <div className="mt-3 flex flex-wrap gap-2">
        {entries.map(([k, v]) => (
          <span key={k} className="rounded-md bg-neutral-100 px-2 py-1 text-xs text-neutral-600">
            <span className="font-medium text-neutral-700">{k}</span>
            {v && <span className="text-neutral-400">: {v}</span>}
          </span>
        ))}
      </div>
    </Panel>
  );
}

// -----------------------------------------------------------------------------
// Delete confirmation (server enforces no-children / no-patients → 409)
// -----------------------------------------------------------------------------

function DeleteLocationDialog({
  open,
  onOpenChange,
  location,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: Location;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const del = useDeleteLocation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.locationDetail.deleteTitle', 'Delete {{name}}?', { name: location.name })}</DialogTitle>
          <DialogDescription>
            {t(
              'org.locationDetail.deleteDesc',
              "This removes the unit and the access that flows through it. A unit with sub-units or admitted patients can't be deleted — move or discharge them first.",
            )}
          </DialogDescription>
        </DialogHeader>
        {del.isError && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMessage(del.error)}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={() =>
              del.mutate(location.id, {
                onSuccess: () => {
                  onOpenChange(false);
                  onDeleted();
                },
              })
            }
            disabled={del.isPending}
          >
            {del.isPending ? <Spinner className="h-4 w-4" /> : t('common.delete', 'Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
